import { Controller } from "@hotwired/stimulus"
import { clearButtonBusy, markButtonBusy } from "../lib/button_loading"
import { get, post } from "@rails/request.js"
import { statusBadgeClass } from "../lib/status_badge"

// The NPN entry behind EVERY "enter your NPN" modal (#15158): the Profile
// "Update NPN" modal (users/_change_npn_modal, #14711) and the NPN purchase
// modal (order_leads_gate/_npn_modal). Both render the shared NPN field
// (shared/_npn_field) and one footer primary, "Verify" (owner: "This should be
// a shared component in a modal popup"). Only the copy, returnTo and the
// purchase modal's open/leave behaviour (npn_purchase_gate_controller) differ.
//
// Entry rules: the input keeps digits only, at most maxLength; Verify stays
// disabled, with the server's entryRule sentence (Nipr::PurchaseGate::ENTRY_RULE)
// as its reason, until the value is minLength–maxLength digits with no leading 0
// (owner: "they enter the required number of digits without a 0"). The lengths
// come from NpnValidationService; the server re-runs its own rules (a leading 0
// is NpnValidationService rule 7) — this only stops a doomed click.
//
// One click (owner: "click Verify"; no confirm): Verify POSTs the candidate to
// validateUrl (Users::ValidateNpnCandidate: the local rules + the NIPR check on
// the unsaved number, behind the per-account five-minute window
// Users::NpnValidateCooldown enforces), polls an accepted check through
// statusUrl (every 1.5 s, at most 45 s, by the server's request_id only), then
// saves through requestUrl (Users::ChangeNpn, which adopts a verdict on file
// without a second billed call; its NIPR-lock refusal stands) and reloads
// returnTo.
//   * Not Found saves NOTHING and keeps the modal (on the buying page the gate
//     stays).
//   * Any answer NIPR could not give for OUR capacity — the daily cap, checks
//     off, an outage or error, the queue, a check still running at the poll
//     deadline, the cool-down store being down — is fail-open: the number
//     passed the format rules, so it is saved, and the background check retries.
//   * The agent's OWN waits never fail open (#15187 review): the five-minute
//     window a previous attempt opened (#14711's penalty for a wrong entry) and
//     a check `scheduled` behind the account's run cool-down both keep the modal
//     and save nothing — otherwise a Not Found number could be beaten by typing
//     a second number at once. The countdown shows, and Verify is disabled with
//     the reason until it ends. The countdown is DISPLAY only: a reload reads
//     the server's remaining seconds from the partial, and an early request is
//     refused with retry_after_seconds, which repaints it.
//
// Every sentence and pill about the NIPR check comes from the server payload
// (Nipr::CandidateStatus) — a local format/availability pass is never shown as a
// NIPR verdict. Feedback is bound to the candidate it was requested for: editing
// the input clears it and drops the flow. Messages and any "Sign in instead?"
// link are built from DOM nodes (never innerHTML / attribute interpolation) per
// the project's XSS-in-attributes rule.
export const ACTION_LABEL = "Verify"

export const REASONS = Object.freeze({
  unchanged: "Enter a different NPN first",
  cooldown: "Please wait for the cool-down to end"
})

export const POLL_INTERVAL_MS = 1500
export const POLL_DEADLINE_MS = 45000
export const POLLED_STATES = Object.freeze(["pending", "scheduled", "running"])
// Compact pill tone per state / verdict (mirrors Nipr::Verdict::TONES for verdicts).
const VERDICT_TONES = Object.freeze({ verified: "success", not_found: "danger", inactive: "danger", not_checked: "secondary" })
const STATE_PILLS = Object.freeze({
  pending: ["pending", "Checking"], running: ["pending", "Checking"],
  scheduled: ["pending", "Scheduled"], skipped: ["warning", "Not checked"], not_started: ["warning", "Not started"],
  error: ["warning", "No answer"], expired: ["warning", "Expired"], superseded: ["warning", "Expired"],
  unavailable: ["warning", "Unavailable"]
})
// Shown for the moment between the poll deadline and the fail-open save.
export const POLL_TIMEOUT_MESSAGE = "Still checking with NIPR. Saving your NPN; the answer is applied when it arrives."
// The verdict that keeps the gate, and the Validate refusals that are our own
// capacity (never a reason to hold). `cooling_down` is NOT one: that window is
// the agent's own previous attempt (#14711).
export const GATING_VERDICTS = Object.freeze(["not_found"])
export const CAPACITY_REFUSALS = Object.freeze(["unavailable"])
// The agent's own waits — Verify saves nothing while one is open.
export const AGENT_WAIT_REFUSALS = Object.freeze(["cooling_down"])
export const AGENT_WAIT_CHECK_STATES = Object.freeze(["scheduled"])
export const NOT_FOUND_MESSAGE = "NIPR has no record of that NPN. Check the number and try again."
export const COOLDOWN_HOLD_MESSAGE = "Your last check was moments ago. Verify again when the countdown ends."
export const SCHEDULED_HOLD_MESSAGE = "NIPR checks one NPN per account at a time. Verify again once the check above has run."
export const GENERIC_ERROR = "Something went wrong. Please try again."

// The entry rule: min–max digits, no leading 0. Pure, so the Jest spec pins it;
// the lengths come from the server (NpnValidationService).
export function entryRuleOk(value, min, max) {
  return /^[1-9]\d*$/.test(value) && value.length >= min && value.length <= max
}

export default class extends Controller {
  static targets = [
    "npnInput", "verifyBtn",
    "cooldown", "cooldownStatus", "message", "check"
  ]
  static values = {
    requestUrl: String, validateUrl: String, statusUrl: String, returnTo: String,
    currentNpn: String, cooldownRemaining: Number,
    minLength: Number, maxLength: Number, entryRule: String
  }

  // Once per controller INSTANCE. Stimulus reuses the instance when the same
  // element is detached and reinserted, so these counters live here and only
  // ever advance — connect() must never reset them, or a continuation captured
  // before a detach would match the generation again after the reinsert.
  initialize() {
    // The lifecycle generation. Every async continuation — a Validate/Save POST,
    // a status GET, a timer tick — captures it when it starts and re-checks it
    // when it lands. modalHidden(), disconnect() and connect() all advance it,
    // so an answer from before a close or a detach touches no messages, starts
    // no poll and saves nothing; `connected` tells a close from a detach apart:
    // a closed but still-connected modal keeps the server's cool-down truth (the
    // window DID open) so a re-open shows it, a disconnected one keeps nothing
    // and owns no timers.
    this.lifecycle = 0
    this.connected = false
    this.requestSeq = 0
    // Busy-state ownership: only the Verify that set the busy state may release
    // it, so an old answer landing after a reconnect cannot open the gate under
    // a Verify issued since.
    this.busyVerify = null
    this.cooldownEndsAt = 0
    this.timer = null
  }

  connect() {
    this.lifecycle += 1
    this.connected = true
    // A reinserted element shows no request's busy state: whatever was in
    // flight before the detach no longer owns the button.
    this.busyVerify = null
    this.setBusy(false)
    // The window is the server's: on first connect the partial carries the
    // remaining seconds (another tab, a reload); on a reinsert the in-memory
    // deadline is the truth and the rendered attribute is stale.
    const remaining = this.cooldownEndsAt > 0 ? (this.cooldownEndsAt - Date.now()) / 1000 : this.cooldownRemainingValue
    this.startCooldown(remaining, { announce: false })
    this.refreshGates()
  }

  disconnect() {
    this.connected = false
    this.lifecycle += 1
    this.stopTimer()
    this.stopPolling()
  }

  // Bootstrap's hidden.bs.modal: a closed modal polls nothing and accepts no
  // late answer as its own; the cool-down countdown keeps running (the element
  // is still in the page and the window is a server fact).
  modalHidden() {
    this.lifecycle += 1
    this.stopPolling()
  }

  // True while the continuation that captured `generation` may still act.
  alive(generation) {
    return this.connected && generation === this.lifecycle
  }

  // A new candidate invalidates whatever the last answer said about the old one.
  inputChanged() {
    this.sanitizeInput()
    this.requestSeq += 1
    this.stopPolling()
    this.clearMessage()
    this.clearCheck()
    this.refreshGates()
  }

  // ── Verify = Validate → the NIPR check → Save (see the header) ───────────

  async verify() {
    if (this.verifyReason()) return
    const npn = this.candidate()
    const generation = this.lifecycle
    const seq = ++this.requestSeq
    this.busyVerify = seq
    this.setBusy(true)
    this.clearMessage()
    this.clearCheck()
    try {
      const response = await post(this.validateUrlValue, { body: { npn_id: npn }, responseKind: "json" })
      const data = await response.json
      if (!this.connected) return // detached: nothing to paint, no timers to own
      // The window is a server fact whatever the answer was for: an accepted
      // attempt opens it (cooldown_seconds), a refused one reports it
      // (retry_after_seconds) — kept even for a closed modal, so a re-open is truthful.
      const seconds = data.cooldown_seconds || data.retry_after_seconds
      if (seconds) this.startCooldown(seconds, { announce: this.alive(generation) })
      if (!this.current(npn, generation, seq)) return this.release(seq)
      if (!data.valid) {
        if (CAPACITY_REFUSALS.includes(data.error_code)) return await this.save(npn, generation, seq)
        if (AGENT_WAIT_REFUSALS.includes(data.error_code)) return this.hold(COOLDOWN_HOLD_MESSAGE, seq)
        this.showError(data)
        return this.release(seq)
      }
      // A check waiting on the account's run cool-down is the agent's own wait:
      // show it and hold (no 45 s poll of a check that cannot run sooner).
      if (data.check && AGENT_WAIT_CHECK_STATES.includes(data.check.state)) {
        this.renderCheck(data.check)
        return this.hold(SCHEDULED_HOLD_MESSAGE, seq)
      }

      const settled = await this.settleCheck(data.check, npn, seq, generation)
      if (!this.current(npn, generation, seq)) return this.release(seq)
      if (this.gatingVerdict(settled)) return this.hold(NOT_FOUND_MESSAGE, seq)
      return await this.save(npn, generation, seq)
    } catch {
      if (this.alive(generation) && seq === this.requestSeq) this.showMessage(GENERIC_ERROR, "error")
      return this.release(seq)
    }
  }

  // Save through the change_npn endpoint; success reloads returnTo. The busy
  // state is kept on success: the page is leaving. The change is committed on
  // the server whatever the modal did meanwhile, so a connected page reloads
  // even if it was closed; a detached one has nothing to do.
  async save(npn, generation, seq) {
    const response = await post(this.requestUrlValue, { body: { npn_id: npn }, responseKind: "json" })
    const data = await response.json
    if (!this.connected) return
    if (data.success) {
      if (this.alive(generation)) this.showMessage("NPN saved. Reloading…", "success")
      window.location.assign(this.returnToValue)
      return
    }
    if (this.alive(generation) && seq === this.requestSeq) this.showError(data)
    this.release(seq)
  }

  // The NIPR check's final payload: at once for a terminal state, else after
  // polling (null on the deadline or when a newer candidate / a close stopped it).
  settleCheck(check, npn, seq, generation) {
    if (!check) return Promise.resolve(null)
    return new Promise((resolve) => this.showCheck(check, npn, seq, generation, resolve))
  }

  gatingVerdict(payload) {
    return Boolean(payload && (payload.state === "cached" || payload.state === "recorded") &&
      GATING_VERDICTS.includes(payload.verdict))
  }

  // Nothing is saved, the gate stays, and the modal says why.
  hold(text, seq) {
    this.showMessage(text, "error")
    return this.release(seq)
  }

  current(npn, generation, seq) {
    return this.alive(generation) && seq === this.requestSeq && npn === this.candidate()
  }

  // Only the Verify that owns the busy state releases it (see initialize()).
  release(seq) {
    if (!this.connected || this.busyVerify !== seq) return
    this.busyVerify = null
    this.setBusy(false)
  }

  // ── gates ────────────────────────────────────────────────────────────────

  candidate() {
    return this.npnInputTarget.value.trim()
  }

  verifyReason() {
    if (this.cooldownSeconds > 0) return REASONS.cooldown
    const npn = this.candidate()
    if (!entryRuleOk(npn, this.minLengthValue, this.maxLengthValue)) return this.entryRuleValue
    // The server's "already your current NPN" compares the trimmed entry with
    // the STORED value as is, so this does too: a stored "17654321 "
    // (malformed, so gated) must stay re-enterable as "17654321".
    if (npn === this.currentNpnValue) return REASONS.unchanged
    return null
  }

  // Digits only, at most maxLength; the live hint marks a value that is not yet acceptable.
  sanitizeInput() {
    const input = this.npnInputTarget
    const clean = input.value.replace(/\D/g, "").slice(0, this.maxLengthValue)
    if (clean !== input.value) input.value = clean
    const invalid = clean !== "" && !entryRuleOk(clean, this.minLengthValue, this.maxLengthValue)
    input.classList.toggle("is-invalid", invalid)
    if (invalid) input.setAttribute("aria-invalid", "true")
    else input.removeAttribute("aria-invalid")
  }

  // A11Y-1 / BUTTON-4: a disabled control explains why (gl-tooltip + accessible name); an enabled one drops the reason.
  // A busy Verify is not a gated one (BUTTON-8): it stays disabled with the
  // shared spinner + aria-busy and its label, and carries no reason.
  refreshGates() {
    const button = this.verifyBtnTarget
    if (this.busy) {
      button.disabled = true
      button.removeAttribute("data-gl-tooltip")
      button.removeAttribute("aria-label")
      return
    }
    const reason = this.verifyReason()
    button.disabled = Boolean(reason)
    if (reason) {
      button.setAttribute("data-gl-tooltip", reason)
      button.setAttribute("aria-label", `${ACTION_LABEL} — ${reason}`)
    } else {
      button.removeAttribute("data-gl-tooltip")
      button.removeAttribute("aria-label")
    }
  }

  setBusy(busy) {
    this.busy = busy
    if (busy) markButtonBusy(this.verifyBtnTarget)
    else clearButtonBusy(this.verifyBtnTarget)
    this.refreshGates()
  }

  // ── cool-down countdown (display of the server's window) ─────────────────

  startCooldown(seconds, { announce = true } = {}) {
    this.stopTimer()
    this.cooldownSeconds = Math.max(0, Math.ceil(Number(seconds) || 0))
    if (this.cooldownSeconds === 0) { this.renderCooldown(); this.refreshGates(); return }
    this.cooldownEndsAt = Date.now() + this.cooldownSeconds * 1000
    if (announce) this.announce(`${ACTION_LABEL} is paused. You can verify again in ${this.words(this.cooldownSeconds)}.`)
    this.renderCooldown()
    this.timer = setInterval(() => this.tick(), 1000)
    this.refreshGates()
  }

  tick() {
    if (!this.connected) { this.stopTimer(); return }
    this.cooldownSeconds = Math.max(0, Math.ceil((this.cooldownEndsAt - Date.now()) / 1000))
    this.renderCooldown()
    if (this.cooldownSeconds === 0) {
      this.stopTimer()
      this.announce("You can verify again now.")
      this.refreshGates()
    }
  }

  stopTimer() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }

  renderCooldown() {
    const el = this.cooldownTarget
    if (this.cooldownSeconds > 0) {
      el.classList.remove("d-none")
      el.textContent = `You can verify again in ${this.clock(this.cooldownSeconds)}.`
      el.setAttribute("aria-label", `You can verify again in ${this.words(this.cooldownSeconds)}`)
    } else {
      el.classList.add("d-none")
      el.textContent = ""
      el.removeAttribute("aria-label")
    }
  }

  // Polite one-off announcements (start / end of the wait); the ticking timer itself is never live.
  announce(text) {
    const el = this.cooldownStatusTarget
    el.textContent = ""
    el.textContent = text
  }

  clock(total) {
    const minutes = Math.floor(total / 60)
    const seconds = total % 60
    return `${minutes}:${String(seconds).padStart(2, "0")}`
  }

  words(total) {
    const minutes = Math.floor(total / 60)
    const seconds = total % 60
    const parts = []
    if (minutes) parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`)
    if (seconds || !minutes) parts.push(`${seconds} second${seconds === 1 ? "" : "s"}`)
    return parts.join(" ")
  }

  // ── the NIPR check on the unsaved candidate ──────────────────────────────

  // `onSettled` receives the terminal payload, or null when the poll hit its
  // deadline or was stopped / superseded.
  showCheck(check, npn, seq, generation, onSettled) {
    this.renderCheck(check)
    if (POLLED_STATES.includes(check.state) && check.request_id) {
      this.startPolling(check.request_id, npn, seq, generation, onSettled)
    } else {
      onSettled(check)
    }
  }

  startPolling(requestId, npn, seq, generation, onSettled) {
    if (!this.alive(generation)) { onSettled(null); return } // a closed or detached modal never starts a poll
    this.stopPolling()
    this.pollSettle = onSettled
    const deadline = Date.now() + POLL_DEADLINE_MS
    const token = { requestId, npn, seq }
    this.pollToken = token
    const tick = async () => {
      this.pollTimer = null
      if (this.pollToken !== token || !this.alive(generation)) return
      if (Date.now() >= deadline) {
        this.pollToken = null
        this.renderCheck({ state: "pending", message: POLL_TIMEOUT_MESSAGE })
        this.resolvePoll(null)
        return
      }
      const payload = await this.fetchStatus(requestId)
      if (this.pollToken !== token) return
      // Dropped when a newer candidate, request generation or modal lifecycle superseded this request in flight.
      if (!this.alive(generation) || npn !== this.candidate() || seq !== this.requestSeq) { this.stopPolling(); return }
      if (payload) this.renderCheck(payload)
      if (!payload || POLLED_STATES.includes(payload.state)) {
        this.pollTimer = setTimeout(tick, POLL_INTERVAL_MS)
      } else {
        this.pollToken = null
        this.resolvePoll(payload)
      }
    }
    this.pollTimer = setTimeout(tick, POLL_INTERVAL_MS)
  }

  stopPolling() {
    if (this.pollTimer) clearTimeout(this.pollTimer)
    this.pollTimer = null
    this.pollToken = null
    this.resolvePoll(null)
  }

  // Hands the poll's outcome to whoever awaits it, once.
  resolvePoll(payload) {
    const settle = this.pollSettle
    this.pollSettle = null
    settle?.(payload)
  }

  // Only the request id the server issued is ever sent — never the NPN.
  async fetchStatus(requestId) {
    try {
      const response = await get(this.statusUrlValue, { query: { request_id: requestId }, responseKind: "json" })
      return await response.json
    } catch {
      return null
    }
  }

  // A compact status pill (gl-status-badge) + the server's sentence, DOM-built.
  renderCheck(check) {
    if (!check || !check.state) { this.clearCheck(); return }
    const el = this.checkTarget
    const [tone, label] = this.pillFor(check)
    const pill = document.createElement("span")
    pill.className = `${statusBadgeClass(tone, { size: "sm" })} me-2`
    pill.setAttribute("data-check-state", check.state)
    pill.textContent = label
    el.replaceChildren(pill, document.createTextNode(check.message || ""))
    el.classList.remove("d-none")
  }

  pillFor(check) {
    if ((check.state === "cached" || check.state === "recorded") && check.verdict) {
      return [VERDICT_TONES[check.verdict] || "secondary", check.verdict_label || check.verdict]
    }
    return STATE_PILLS[check.state] || ["warning", "Not checked"]
  }

  clearCheck() {
    if (!this.hasCheckTarget) return
    this.checkTarget.classList.add("d-none")
    this.checkTarget.replaceChildren()
  }

  // ── messages ─────────────────────────────────────────────────────────────

  // server strings via textContent; link href via setAttribute — never innerHTML
  showError(data) {
    this.clearMessage()
    this.messageTarget.classList.remove("d-none")
    this.messageTarget.className = "gl-onboarding-message error mt-3"
    this.messageTarget.replaceChildren(document.createTextNode(data.message || "Could not update your NPN."))
    // #15679: the NPN is on another account. Say which one (masked), whether a
    // sign-in link went to it, and how to get the two merged.
    const recovery = data.recovery
    if (recovery && recovery.masked_email) {
      const sent = recovery.email_sent ? " We emailed that account a sign-in link." : ""
      this.messageTarget.append(
        ` It is the account for ${recovery.masked_email}.${sent}` +
        " If both accounts are yours, contact support and we will merge them."
      )
    }
    if (data.sign_in_url) {
      const link = document.createElement("a")
      link.setAttribute("href", data.sign_in_url)
      link.className = "gl-onboarding-link ms-1"
      link.textContent = "Sign in instead?"
      this.messageTarget.append(" ", link)
    }
  }

  showMessage(text, type) {
    this.messageTarget.classList.remove("d-none")
    this.messageTarget.className = `gl-onboarding-message ${type} mt-3`
    this.messageTarget.replaceChildren(document.createTextNode(text))
  }

  clearMessage() {
    this.messageTarget.classList.add("d-none")
    this.messageTarget.replaceChildren()
  }
}
